import { useEffect, useState } from 'react';
import { useApi } from '@/hooks/useApi';
import { usePagination } from '@/hooks/usePagination';
import { useDebounce } from '@/hooks/useDebounce';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { DataTable } from '@/components/common/DataTable';
import { DriverFilterSidebar } from '@/components/features/drivers/DriverFilterSidebar';
import { AddDriverModal } from '@/components/features/drivers/AddDriverModal';
import { UserX, Filter, Eye, Plus, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { useI18n } from '@/contexts/I18nContext';

interface Driver {
  id: string;
  name: string;
  phone: string;
  payment_type: 'commission' | 'salary';
  commission_rate: string;
  created_at: string;
}

interface FilterValues {
  search: string;
  paymentType: string;
}

const initialFilters: FilterValues = {
  search: '',
  paymentType: '',
};

export default function DriversList() {
  const { get, loading } = useApi();
  const pagination = usePagination();
  const navigate = useNavigate();
  const { tp } = useI18n();

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [filters, setFilters] = useState<FilterValues>(initialFilters);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);

  const debouncedSearch = useDebounce(filters.search, 300);

  useEffect(() => {
    fetchDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, debouncedSearch, filters.paymentType]);

  const fetchDrivers = async () => {
    try {
      const params: Record<string, any> = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      if (filters.paymentType) {
        params.payment_type = filters.paymentType;
      }

      const response: any = await get(
        '/api/v1/dashboard/drivers',
        params
      );

      if (response?.data && Array.isArray(response.data)) {
        setDrivers(response.data);
        pagination.updatePagination({
          total_pages: response.pages || 0,
          total: response.total || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch drivers:', error);
    }
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    pagination.reset();
  };

  const handleFilterChange = (key: keyof FilterValues, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === 'all' ? '' : value,
    }));
  };

  const handleDriverClick = (driver: Driver) => {
    navigate(`/dashboard/drivers/${driver.id}`);
  };

  const columns = [
    {
      header: tp.drivers.tableHeaderName,
      accessor: 'name' as keyof Driver,
      className: 'font-medium',
    },
    {
      header: tp.drivers.tableHeaderPhone,
      accessor: 'phone' as keyof Driver,
    },
    {
      header: tp.drivers.tableHeaderPaymentType,
      accessor: (row: Driver) => (
        <Badge 
          variant="outline" 
          className={row.payment_type === 'commission' 
            ? 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800' 
            : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800'
          }
        >
          {row.payment_type === 'commission' ? tp.drivers.paymentTypeCommission : tp.drivers.paymentTypeSalary}
        </Badge>
      ),
    },
    {
      header: tp.drivers.tableHeaderCommissionRate,
      accessor: (row: Driver) => row.payment_type === 'commission' ? `${(parseFloat(row.commission_rate) * 100).toFixed(0)}%` : '-',
    },
    {
      header: tp.drivers.tableHeaderCreatedDate,
      accessor: (row: Driver) => new Date(row.created_at).toLocaleDateString('ar-EG'),
      className: 'text-muted-foreground',
    },
  ];

  const renderActions = (row: Driver) => (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      onClick={(e) => { e.stopPropagation(); handleDriverClick(row); }}
      title={tp.drivers.viewDetailsTooltip}
    >
      <Eye className="h-4 w-4" />
    </Button>
  );

  const hasActiveFilters = filters.search !== '' || filters.paymentType !== '';

  const FilterSidebarContent = (
    <DriverFilterSidebar
      filters={filters}
      onFilterChange={handleFilterChange}
      onClearFilters={handleClearFilters}
    />
  );

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronRight className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">{tp.drivers.pageTitle}</h1>
              <p className="text-sm text-muted-foreground">{tp.drivers.pageSubtitle}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="h-4 w-4 ms-2" />
              {tp.drivers.addDriverButton}
            </Button>
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden">
                  <Filter className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-4">
                <div className="mt-6">
                  {FilterSidebarContent}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <div className="flex gap-4 h-[calc(100vh-180px)]">
        <div className="hidden lg:block h-full overflow-y-auto">
          {FilterSidebarContent}
        </div>
        <div className="flex-1 min-w-0">
          {loading ? (
            <LoadingSkeleton type="table" rows={10} />
          ) : drivers.length === 0 ? (
            <EmptyState
              icon={<UserX className="h-16 w-16" />}
              title={tp.drivers.emptyStateTitle}
              description={tp.drivers.emptyStateDescription}
              action={
                hasActiveFilters
                  ? { label: tp.commonExt.clearFilters, onClick: handleClearFilters }
                  : undefined
              }
            />
          ) : (
            <DataTable
              data={drivers}
              columns={columns}
              selectable={true}
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
              actions={renderActions}
              pagination={{
                currentPage: pagination.page,
                totalPages: pagination.totalPages,
                total: pagination.total,
                onPageChange: pagination.goToPage,
              }}
              emptyMessage={tp.drivers.emptyStateTitle}
            />
          )}
        </div>
      </div>

      <AddDriverModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        onSuccess={fetchDrivers}
      />
    </PageContainer>
  );
}
